import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ChevronDownIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const Footer: React.FC = () => {
  const location = useLocation();
  const [selectedRegion, setSelectedRegion] = useState('USA - EN');

  // Hide footer when on /custom/* routes
  if (location.pathname.startsWith('/custom')) {
    return null;
  }

  const footerLinks = [
    {
      title: 'About',
      links: [
        { name: 'About', href: '/about' },
        { name: 'Corporate Responsibility', href: '/contact' }, 
        { name: 'Sell on GiftMkr', href: '/referral-program' }
      ]
    },
    {
      title: 'Customer Service',
      links: [
        { name: 'My Account', href: '/account' }, 
        { name: 'Track my order', href: '/track-order' },
        { name: 'Return Policy', href: '/return-policy' }
      ]
    },
    {
      title: 'Support',
      links: [
        { name: 'Sitemap', href: '/sitemap' },
        { name: 'Help', href: '/help' },
        { name: 'Careers', href: '/careers' },
        { name: 'Contact Us', href: '/contact' }
      ]
    },
    {
      title: 'Contact',
      links: [
        { name: 'legal@giftmkr.com', href: 'mailto:legal@giftmkr.com' }
      ]
    }
  ];

  const socialMediaLinks = [
    { name: 'Facebook', icon: 'facebook', href: 'https://facebook.com' },
    { name: 'Instagram', icon: 'instagram', href: 'https://instagram.com' },
    { name: 'Pinterest', icon: 'pinterest', href: 'https://pinterest.com' },
    { name: 'YouTube', icon: 'youtube', href: 'https://youtube.com' },
    { name: 'TikTok', icon: 'tiktok', href: 'https://tiktok.com' }
  ];

  const appStoreLinks = [
    { name: 'App Store', icon: 'apple', href: 'https://apps.apple.com' },
    { name: 'Google Play', icon: 'google', href: 'https://play.google.com' }
  ];

  return (
    <footer className="text-white" style={{ backgroundColor: '#2e3a47' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Top Section - Logo and Slogan */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <img src="/images/bottom-logo.png" alt="GiftMkr" className="mx-auto h-12 w-auto" />
            <p className="text-sm text-gray-300 mt-1">Create your moment™</p>
          </div>
          <div className="w-full h-px bg-white opacity-30"></div>
        </div>

        {/* Middle Section - Navigation Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {footerLinks.map((section, index) => (
            <div key={index}>
              <h3 className="font-semibold text-white mb-4">{section.title}</h3>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      to={link.href}
                      className="text-gray-300 hover:text-white transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Region and Language Selector */}
        <div className="text-center mb-8">
          <p className="text-white mb-3">Choose a region and language</p>
          <div className="inline-flex items-center bg-white px-4 py-2 rounded cursor-pointer hover:bg-gray-100 transition-colors" style={{ color: '#2e3a47' }}>
            <GlobeAltIcon className="w-4 h-4 mr-2" />
            <span className="mr-2">{selectedRegion}</span>
            <ChevronDownIcon className="w-4 h-4" />
          </div>
        </div>

        {/* Bottom Section - Social Media and App Store Links */}
        <div className="text-center">
          {/* Social Media Icons */}
          <div className="flex justify-center items-center space-x-4 mb-6">
            {socialMediaLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white transition-colors group"
                style={{ color: 'white' } as React.CSSProperties}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#2e3a47';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'white';
                }}
                title={social.name}
              >
                <span className="text-lg font-bold">{social.name.charAt(0).toUpperCase()}</span>
              </a>
            ))}
          </div>

          {/* App Store Links */}
          <div className="flex justify-center items-center space-x-4">
            {appStoreLinks.map((app, index) => (
              <a
                key={index}
                href={app.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-black text-white px-4 py-2 rounded border border-white hover:bg-gray-800 transition-colors"
              >
                <span className="mr-2 font-bold">{app.name.charAt(0)}</span>
                <span className="text-sm font-medium">{app.name}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 